import { css } from "@emotion/css";
import type { CSSObject } from "@emotion/css/create-instance";
import {palette as palette2} from 'Shared/artists/Palette';
import { sanitise } from 'Shared/common/Sanitise';


/* Palette */
function palette() 
{
	return {
		background: 'grey',
		dimBackground: '#f5f5f5',   //XXX alt name - could be 'shaded'
		frontendBackground: 'black', //CJ added because we need poster previews to be viewed in context of the frontend background
		text: 'black', 
		dimText: '#aaa',		// e.g. disabled
		label: '#666',

		borderBackgroundColour: '#1b2126',

		subHeaderBackground: '#fafafa',

		//XXX cf relationship with IconColour
		active: '#44C4A1',
		inactive: 'lightgrey',

		link: '#005394',
		error: '#ff0101'
	};
}

export function sizes()
{
	return {
		mobile: 600,
		endFontShrink: 375,
		minDesktopWidth: 1000,
		maxBodyWidth:  1100
	};
}


const dashboardFont = '"Open Sans",Helvetica,Arial,sans-serif';

/*
	The dashboard arrived with its own visual language - a cool slate scale with a blue accent -
	rather than the warmer greys the rest of /admin uses, and it keeps it. What it does not keep is
	95 hex literals scattered over 50 files. These are the structural colours: surfaces, borders,
	text ranks and the status accents. Genuinely one-off decoration (the Instagram gradient stops,
	for instance) stays inline where it is written, because naming it would say nothing.
*/
function dashboardPalette()
{
	return {
		white: '#ffffff',
		black: '#000000',

		/* The dark ground of the dashboard's own buttons - softer than pure black */
		buttonDark: '#1b2126',

		/* Surfaces, lightest first */
		page: '#f8fafc',
		surface: '#f1f5f9',
		surfaceRaised: '#ffffff',

		/* Borders, lightest first */
		border: '#e2e8f0',
		borderStrong: '#cbd5e1',

		/* Text, faintest first */
		textFaint: '#94a3b8',
		textMuted: '#64748b',
		textSubtle: '#475569',
		textStrong: '#334155',
		inkSoft: '#1e293b',
		ink: '#0f172a',

		accent: '#2563eb',
		accentStrong: '#1d4ed8',
		accentSoft: '#60a5fa',
		accentWash: '#eff6ff',

		/* The underline/hover colour on record links */
		highlight: '#4f46e5',

		danger: '#ef4444',
		dangerStrong: '#dc2626',
		dangerDeep: '#991b1b',
		dangerWash: '#fef2f2',
		dangerBorder: '#fecaca',

		success: '#10b981',
		successStrong: '#15803d',
		successWash: '#f0fdf4',

		warning: '#facc15',
		info: '#0284c7'
	};
}

function button()  
{
	return {
		textAlign: 'center',
		verticalAlign: 'middle',
		backgroundColor: palette2.button,
	//	backgroundColor: '#7e94a5',
		borderRadius: 3,
		border: '1px solid #728a9d',
		color: '#FFF',
		padding: '8px 12px',
		fontSize: 13,
		fontWeight: 400,
		lineHeight: '16px',
		userSelect: 'none',
		whiteSpace: 'noWrap',

		'&:hover:not([disabled])': {
			cursor: 'pointer',

			backgroundColor: '#627b8e',
			borderColor: '#566b7c',
		},

		'&[disabled]': {
			filter: 'brightness(125%)',

			'&:hover': {
				cursor: 'not-allowed'
			}
		},

		'&:focus': {
			border: `solid 1px brightness(70%)`
		}
	} satisfies CSSObject;
}


export function backendTheme()  //XXX should implement ITheme, but having trouble 
{
	return {
		palette: palette(),
		sizes: sizes(),
		//TODO
		//fonts:fonts,

		root: {
			font: '15px "Open Sans",Helvetica,Arial,sans-serif',
			backgroundColor: 'white',
			color: 'black'
		},

		nav: {
			height: '70px'
		},

		content: {
			paddingTop: '2em'
		},

		heading: {
			color: 'black',
			fontWeight: 600
		},

		inverseHeading: {
			margin: '1em 1em 1em 0',

			select: {
				fontSize: '1.1em',
				background: '#1b2126',
				color: 'white !important',
				border: 'none'
			}
		},

		fieldWrap: {
			display: 'block',
			marginBottom: 15,
			width: '100%',
			padding: 6
		},

	//	'button, input[type="submit"], input[type="button"]': {
		button: button(),

		smallButton: {...button(),
			padding: '6px 0.6em',
			fontSize: '0.7em'
		},

		a: {
			...sanitise.a,
			color: palette().link,
			cursor: 'pointer',

			'&:visited': {
				color: palette().link
			}
		},

		section: {
	//XXX I suspect the blue on is really the main case, and the other one an exception. cf renaming
			blueHeading: {
				fontSize: '1.2em',
				color: '#42627b',
				lineHeight: '1.5em',
				textTransform: 'uppercase',
				textAlign: 'left',
				fontWeight: 'initial'
			} satisfies CSSObject,

			heading: {
				color: 'black',
				textTransform: 'uppercase',
				fontSize: 15,
				fontWeight: 600,
				margin: '12px 0'
			},
		},

		menu: {
			heading: {
				fontSize: '1.1em',
				fontWeight: 'normal',
				margin: '2em 0 1em 0',
				color: '#7e94a5'
			}
		},

		detailsPane: {
			border: '1px solid #eee',
			borderBottom: 'none',
			padding: '1em 1em 0 1em',
			minHeight: 315,
			/* Allow the active tab to cut of hole in the bottom border */ 
			zIndex: -1
		},

		linkIcon: {
			width: '2em'
		},

		warning: {
			color: palette().error
		},

		dashboard: {
			palette: dashboardPalette(),

			/* The dashboard's modals render through portals, so they sit outside root and have to
			   be given the font by their own styles. */
			fontFamily: dashboardFont,

			/* Stops iOS zooming the page when a field takes focus - it zooms anything under 16px.
			   Spread into root and into every portalled backdrop, for the same reason as fontFamily. */
			noFocusZoom: {
				'@media screen and (max-width: 768px)': {
					'input, select, textarea': {
						fontSize: '16px'
					}
				}
			},

			/* Carries what index.css used to apply to <body>. Kept here so the page has no global
			   CSS of its own - index.css is gone. */
			root: {
				fontFamily: dashboardFont,
				backgroundColor: dashboardPalette().page,
				color: dashboardPalette().ink,
				WebkitFontSmoothing: 'antialiased',
				MozOsxFontSmoothing: 'grayscale',

				'input, select, textarea, button': {
					fontFamily: 'inherit',
					touchAction: 'manipulation'
				}
			}
		}
	};
}

export function verticalStyle(gap='1em') 
{
	return css({
		display: 'flex',
		flexDirection: 'column',
		gap: gap
	});
}

export function inlineOpenLinkStyle() 
{
	return css(backendTheme().a, {
		display: 'inline-flex',
		alignItems: 'center',
		gap: '0.5em',
	//	padding: '0 0.6em'
	});
}

